import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { lerCronogramaDoProduto, type Catalogo, type CursoDoCatalogo, type TurmaDoCronograma } from "../lib/adminCatalogo";
import { lerHistoricoDoProduto, type MudancaDoProduto } from "../lib/documentosDoProduto";
import { ehFerramentaDeCurso, Prosa, situacaoDaFerramenta } from "./AdminCatalogoUi";
import AdminCatalogoProdutoEditar from "./AdminCatalogoProdutoEditar";
import AdminCatalogoProdutoPagina from "./AdminCatalogoProdutoPagina";
import AdminCatalogoProdutoDocumentos from "./AdminCatalogoProdutoDocumentos";
import { IndicadoresDoProduto } from "./AdminCatalogoProdutoPesquisas";
import AdminCatalogoProdutoProducao from "./AdminCatalogoProdutoProducao";

/**
 * Admin › Catálogo › Produtos › **o produto** (000262).
 *
 * ---------------------------------------------------------------------------
 * Um produto tem DOIS lados, e esta tela mostra os dois lado a lado
 * ---------------------------------------------------------------------------
 *   · O que ele É — a PÁGINA do produto, escrita por quem vende e ensina:
 *     objetivo, público, ementa, carga horária, preço, professor — e os
 *     DOCUMENTOS dele (a ementa, o projeto pedagógico, o registro no MEC).
 *   · O que ele FAZ no hub — os SKUs, os ids da Guru e da TMB, o contrato, o
 *     prazo acadêmico que a emissão cobra, e as ferramentas que ele libera.
 *
 * Ter os dois na mesma tela é o ponto. Enquanto eles moravam em dois sistemas,
 * ninguém olhava os dois juntos — e a primeira vez que alguém olhou, na
 * conferência que esta tela mostra, apareceu uma pós cuja página promete 24
 * meses de prazo e cujo banco guarda 18.
 *
 * ---------------------------------------------------------------------------
 * A página é EDITADA AQUI desde 25/09 (000306)
 * ---------------------------------------------------------------------------
 * Até 24/09 ela era ESPELHO somente leitura do ClickUp, recarregado por
 * `etl/carregar-catalogo-doc.mjs` (000262). A decisão 4 de Victor, de 24/09 —
 * "a página do produto deixa de ser espelho do ClickUp" —, virou a origem: as
 * onze páginas passaram a `origem = 'hub'`, o que veio de lá é a importação de
 * 16/09, e a carga RECUSA página do hub em vez de sobrescrevê-la.
 *
 * A tela continua dizendo a procedência — de onde veio, quando foi editada e
 * por quem —, porque sem ela daqui a seis meses ninguém sabe se o preço na tela
 * é o de hoje.
 *
 * ---------------------------------------------------------------------------
 * Por CURSO, não por SKU
 * ---------------------------------------------------------------------------
 * A Escola é dois produtos (`escola-contabil` e `escola-vitalicio`) e uma
 * página. O aluno vê um curso; quem agrupa é `produtos.curso_slug`. Uma tela
 * por SKU daria duas cópias do mesmo texto, e a que divergisse seria a que
 * ninguém lê.
 *
 * ---------------------------------------------------------------------------
 * O lado do HUB é EDITÁVEL, e é aqui que se edita (21/09)
 * ---------------------------------------------------------------------------
 * Pedido de Victor: *"a tela de produtos deve ter uma tela de edição dedicada
 * assim como nas ferramentas, quando clicamos no botão Abrir"*. O formulário
 * que morava na LISTA saiu no mesmo ato — duas portas para a mesma ação é o
 * defeito que esta casa evita por regra, e a segunda é sempre a que envelhece.
 *
 * Os dois lados agora são editáveis, e por portas DIFERENTES: a página tem o
 * editor dela (`?pagina=editar`), e cada SKU tem o dele (`?sku=`). São atos
 * diferentes, com linhas diferentes no histórico do produto, embaixo.
 *
 * Quem se edita é UM SKU, escolhido por `?sku=<slug>` — ver
 * `AdminCatalogoProdutoEditar`.
 */

/** Uma linha da ficha. Só aparece com valor — campo vazio não vira traço. */
function Linha({ rotulo, valor }: { rotulo: string; valor: React.ReactNode }) {
  if (valor === null || valor === undefined || valor === "") return null;
  return (
    <div className="flex flex-wrap gap-x-2 py-1.5">
      <dt className="min-w-36 text-sm text-cinza">{rotulo}</dt>
      <dd className="text-sm font-semibold text-tinta">{valor}</dd>
    </div>
  );
}

export default function AdminCatalogoProduto({
  catalogo,
  curso,
  voltar,
  recarregar,
  veFeedbacks = false,
}: {
  catalogo: Catalogo;
  curso: string;
  /** O caminho de volta para a lista de produtos. */
  voltar: string;
  recarregar: () => Promise<void>;
  /** Quem vê a aba Feedbacks, onde moram as pesquisas do curso desde a 000332. */
  veFeedbacks?: boolean;
}) {
  // O SKU em edição vive na QUERY, ao lado do curso, pela mesma razão que a
  // sub-aba é sub-rota: o link é compartilhável e o botão do navegador volta
  // para a ficha em vez de sair da página. Os hooks vêm ANTES de qualquer
  // `return` condicional — um `return` no meio deles derruba a rota inteira
  // com o erro React #310, e nem o `tsc` nem a suíte SQL veem isso.
  const [params, setParams] = useSearchParams();
  const skuAberto = params.get("sku") ?? "";
  // O editor da página também vive na QUERY: o link "editar a página de tal
  // curso" é compartilhável, e o botão do navegador fecha o editor.
  const editandoPagina = params.get("pagina") === "editar";
  // O histórico do produto é relido quando o catálogo do pai muda (todo
  // salvamento da página termina em `recarregar()`) e quando um documento muda,
  // que não recarrega o catálogo.
  const [versaoHistorico, setVersaoHistorico] = useState(0);

  /** Põe ou tira o `?sku=` preservando o resto da query (o `produto`). */
  function abrirSku(slug: string | null) {
    const p = new URLSearchParams(params);
    if (slug) p.set("sku", slug);
    else p.delete("sku");
    setParams(p);
  }

  /** Abre ou fecha o editor da página, preservando o resto da query. */
  function abrirEditorDaPagina(abrir: boolean) {
    const p = new URLSearchParams(params);
    if (abrir) p.set("pagina", "editar");
    else p.delete("pagina");
    setParams(p);
  }

  const c: CursoDoCatalogo | undefined = catalogo.cursos.find((x) => x.slug === curso);

  if (!c) {
    return (
      <div className="surface rounded-cf-lg p-6" data-produto-inexistente>
        <h2 className="font-display text-lg font-bold text-titulo">Este curso não existe</h2>
        <p className="mt-2 text-sm text-cinza">
          Nenhum produto tem <code>curso_slug = {curso}</code>.
        </p>
        <Link to={voltar} className="btn-contorno mt-4 inline-flex min-h-10 items-center text-sm">
          Voltar aos produtos
        </Link>
      </div>
    );
  }

  const doc = c.doc;
  const skus = catalogo.produtos.filter((p) => p.curso_slug === c.slug);
  // Pessoas distintas, do BANCO (000290): somar os SKUs contava duas vezes
  // quem tem os dois produtos do mesmo curso.
  const alunos = c.alunos;
  const ids = catalogo.ids.filter((i) => i.curso_slug === c.slug);
  // As FERRAMENTAS que este curso entrega. Quem decide o que conta é
  // `ehFerramentaDeCurso`, num lugar só: os módulos da FBC (Minhas Notas,
  // Secretaria do Aluno) são fixos em todas as pós e no MBA, e apareciam aqui
  // até 21/09 — a lista dizia "2 card(s)" num curso que não tem ferramenta
  // nenhuma própria.
  const cards = catalogo.cards.filter(
    (k) => ehFerramentaDeCurso(k) && k.produtos.some((p) => p.curso_slug === c.slug),
  );
  const prazo = c.prazo_academico;

  // As seções com CONTEÚDO. Uma seção vazia é cabeçalho de agrupamento da
  // origem (a página da CGT tem "Proposta:" segurando sub-títulos) e não tem o
  // que mostrar — some em vez de virar um `<details>` que abre no nada.
  const secoes = (doc?.secoes ?? []).filter((s) => s.texto.trim());

  return (
    <div className="flex flex-col gap-5" data-produto={c.slug}>
      <div className="flex flex-wrap items-center gap-3">
        <Link to={voltar} className="btn-contorno inline-flex min-h-10 items-center text-sm" data-voltar>
          ← Produtos
        </Link>
        <p className="text-sm text-cinza">
          {c.faculdade ? "curso de faculdade" : "curso livre"} · {skus.length} SKU(s) ·{" "}
          {alunos.toLocaleString("pt-BR")} com acesso vigente
        </p>
      </div>

      {/* ── Identidade e ficha ────────────────────────────────────────────── */}
      <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-produto">
        <h2 id="titulo-produto" className="font-display text-xl font-bold text-titulo">
          {c.nome}
        </h2>
        <p className="mt-1 font-mono text-sm text-cinza">{c.slug}</p>

        {doc ? (
          <>
            <div className="mt-5">
              <Prosa texto={doc.sobre} />
            </div>

            <dl className="mt-6 border-t border-borda pt-4 sm:columns-2 sm:gap-8">
              <Linha rotulo="Título conferido" valor={doc.titulo_conferido} />
              <Linha rotulo="Modalidade" valor={doc.modalidade} />
              <Linha rotulo="Professor líder" valor={doc.professor_lider} />
              <Linha rotulo="Universidade" valor={doc.universidade} />
              <Linha rotulo="Valor" valor={doc.valor} />
              <Linha rotulo="Lançamento" valor={doc.lancamento} />
              <Linha
                rotulo="Credenciamento MEC"
                valor={
                  doc.mec_url ? (
                    <a
                      href={doc.mec_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-link underline underline-offset-2"
                    >
                      consultar no e-MEC
                      <span className="sr-only"> (abre em outra aba)</span>
                    </a>
                  ) : null
                }
              />
            </dl>

            {/* A PROCEDÊNCIA, e ela é metade do valor da ficha: sem a data,
                daqui a seis meses ninguém sabe se o preço na tela é o de hoje
                ou o de 2024. */}
            <p className="mt-5 border-t border-borda pt-4 text-sm text-cinza" data-procedencia={doc.origem}>
              {doc.lido_em && doc.fonte_url && (
                <>
                  Importada da página do ClickUp em{" "}
                  <strong className="text-tinta">{new Date(doc.lido_em).toLocaleDateString("pt-BR")}</strong>.{" "}
                </>
              )}
              {doc.origem === "hub" ? (
                <>
                  Desde 25/09/2026 esta página é editada aqui, e a carga do ClickUp não a sobrescreve
                  {doc.editado_em ? (
                    <>
                      {" "}— última edição em{" "}
                      <strong className="text-tinta">{new Date(doc.editado_em).toLocaleDateString("pt-BR")}</strong>
                      {doc.editado_por_descricao ? `, por ${doc.editado_por_descricao}` : ""}
                    </>
                  ) : null}
                  .
                </>
              ) : (
                <>A carga do ClickUp ainda escreve nesta página; a primeira edição aqui a traz para o hub.</>
              )}{" "}
              <button
                type="button"
                data-editar-pagina
                onClick={() => abrirEditorDaPagina(!editandoPagina)}
                aria-expanded={editandoPagina}
                className="font-semibold text-link underline underline-offset-2"
              >
                {editandoPagina ? "Fechar o editor" : "Editar a página"}
              </button>
            </p>
          </>
        ) : (
          <p className="mt-4 text-sm text-cinza" data-sem-doc>
            Este curso ainda não tem página. Enquanto isso, o painel mostra só o lado operacional,
            abaixo.{" "}
            <button
              type="button"
              data-editar-pagina
              onClick={() => abrirEditorDaPagina(!editandoPagina)}
              aria-expanded={editandoPagina}
              className="font-semibold text-link underline underline-offset-2"
            >
              {editandoPagina ? "Fechar o editor" : "Criar a página"}
            </button>
          </p>
        )}
      </section>

      {/* ── O editor da página (000306) ───────────────────────────────────── */}
      {editandoPagina && (
        <AdminCatalogoProdutoPagina
          curso={c.slug}
          doc={doc}
          aoSalvar={async () => {
            await recarregar();
            setVersaoHistorico((v) => v + 1);
          }}
          aoFechar={() => abrirEditorDaPagina(false)}
        />
      )}

      {/* ── Os indicadores (000310) ───────────────────────────────────────── */}
      <IndicadoresDoProduto curso={c.slug} alunos={alunos} faculdade={c.faculdade} versao={versaoHistorico} vePesquisas={veFeedbacks} />

      {/* ── Os documentos do produto (000306) ─────────────────────────────── */}
      <AdminCatalogoProdutoDocumentos curso={c.slug} aoMudar={() => setVersaoHistorico((v) => v + 1)} />

      {/* ── A produção de conteúdo (000313) ────────────────────────────────── */}
      <AdminCatalogoProdutoProducao curso={c.slug} aoMudar={() => setVersaoHistorico((v) => v + 1)} />

      {/* ── O cronograma de liberação, por turma (000309) ─────────────────── */}
      {c.faculdade && <CronogramaDoProduto curso={c.slug} />}

      {/* As PESQUISAS de satisfação moravam aqui (000310) e foram para a aba
          Feedbacks em 30/09 (000332), ao lado das avaliações das ferramentas. O
          NPS do produto continua nos indicadores, acima, com o caminho até elas;
          e o histórico abaixo continua contando cada pesquisa aberta e encerrada,
          porque é o razão do produto. */}

      {/* ── A conferência: o que a página promete × o que o banco faz ─────── */}
      {c.faculdade && (
        <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-confere">
          <h3 id="titulo-confere" className="font-display text-lg font-bold text-titulo">
            A página e o banco
          </h3>
          <p className="mt-1 text-sm text-cinza">
            A carga horária e os prazos aparecem nos dois lugares. Quem manda é o banco — é dele
            que a emissão tira o número que vai IMPRESSO no diploma e a trava que recusa
            certificado antes do prazo mínimo.
          </p>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-borda text-left text-xs uppercase text-cinza">
                  <th className="py-2 pr-4 font-semibold">Campo</th>
                  <th className="py-2 pr-4 font-semibold">A página diz</th>
                  <th className="py-2 pr-4 font-semibold">O banco faz</th>
                  <th className="py-2 font-semibold">Confere?</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borda">
                {(
                  [
                    ["carga_horaria", "Carga horária", doc?.carga_horaria_fonte, prazo?.carga_horaria_horas, "h"],
                    ["duracao_minima", "Prazo mínimo", doc?.duracao_minima_fonte, prazo?.meses, " meses"],
                    ["duracao_maxima", "Prazo máximo", doc?.duracao_maxima_fonte, prazo?.meses_maximo, " meses"],
                  ] as const
                ).map(([chave, rotulo, fonte, banco, unidade]) => {
                  const diverge = fonte != null && banco != null && fonte !== banco;
                  const naoDaParaSaber = fonte == null || banco == null;
                  return (
                    <tr key={chave} data-confere={chave}>
                      <td className="py-2 pr-4 text-cinza">{rotulo}</td>
                      <td className="py-2 pr-4 text-tinta">
                        {fonte != null ? `${fonte}${unidade}` : "—"}
                      </td>
                      <td className="py-2 pr-4 text-tinta">
                        {banco != null ? `${banco}${unidade}` : "—"}
                      </td>
                      <td className="py-2">
                        {/* Três estados, e o do meio é o que importa: "não dá
                            para saber" não é "confere". O `coalesce` que
                            esconde a diferença entre "não" e "não sei" é uma
                            cicatriz desta casa, e aqui ela ficaria na direção
                            de calar uma divergência. */}
                        {naoDaParaSaber ? (
                          <span className="text-xs text-cinza">um dos dois não diz</span>
                        ) : diverge ? (
                          <span className="rounded-full bg-erro-fundo px-2 py-0.5 text-xs font-bold text-erro-tinta">
                            diverge
                          </span>
                        ) : (
                          <span className="rounded-full bg-aviso-fundo px-2 py-0.5 text-xs font-semibold text-aviso-tinta">
                            confere
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!prazo && (
            <p className="mt-3 text-sm text-alerta-tinta" data-sem-prazo>
              Este curso não tem prazo em <code>pos_prazos_curso</code>, e a trava 5 da emissão
              recusa TODO certificado dele com <code>prazo_nao_configurado</code>.
            </p>
          )}
        </section>
      )}

      {/* ── O lado operacional ────────────────────────────────────────────── */}
      <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-operacional">
        <h3 id="titulo-operacional" className="font-display text-lg font-bold text-titulo">
          Como ele funciona no hub
        </h3>

        <ul className="mt-4 divide-y divide-borda">
          {skus.map((p) => (
            <li key={p.id} className="py-3" data-sku={p.slug}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <p className="font-semibold text-tinta">
                  {p.nome} <span className="font-mono text-xs text-cinza">{p.slug}</span>
                  {!p.ativo && (
                    <span className="ml-2 rounded-full bg-alerta-fundo px-2 py-0.5 text-xs font-normal text-alerta-tinta">
                      desativado
                    </span>
                  )}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-xs text-cinza">
                    {p.alunos.toLocaleString("pt-BR")} com acesso · {p.ids} id(s) externo(s) ·{" "}
                    {p.prazo_tipo === "vitalicio"
                      ? "vitalício"
                      : p.prazo_tipo === "meses"
                        ? `${p.prazo_meses} meses`
                        : `até ${p.prazo_data_fixa}`}
                  </p>
                  {/* Um BOTÃO, e não um link: ele não navega para outro lugar —
                      abre a edição nesta mesma página, e quem guarda o endereço
                      é a query. O link compartilhável continua existindo (é a
                      URL da barra depois do clique). */}
                  <button
                    type="button"
                    data-editar-produto={p.slug}
                    onClick={() => abrirSku(skuAberto === p.slug ? null : p.slug)}
                    aria-expanded={skuAberto === p.slug}
                    className="btn-contorno min-h-9 shrink-0 text-xs"
                  >
                    {skuAberto === p.slug ? "Fechar" : "Editar"}
                  </button>
                </div>
              </div>
              {p.falta.length > 0 && (
                <ul className="mt-1.5 space-y-1">
                  {p.falta.map((f) => (
                    <li key={f.lugar} className="text-xs text-alerta-tinta" data-falta={f.lugar}>
                      {f.lugar}: {f.diz}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>

        {/* ── A edição do SKU escolhido ──────────────────────────────────
            O `key` é o que RECRIA o rascunho ao trocar de SKU: sem ele, quem
            abrisse a Escola Pro e depois a Vitalícia veria os valores da
            primeira no formulário da segunda, porque o estado inicial de um
            componente montado só é lido uma vez.

            E o SKU que a query nomeia pode não ser deste curso (link velho,
            slug digitado à mão): aí não se edita nada, e a tela DIZ — abrir o
            formulário do produto de outro curso dentro desta ficha seria
            exatamente a "segunda porta" que esta tela existe para fechar. */}
        {skuAberto &&
          (skus.some((p) => p.slug === skuAberto) ? (
            <div className="mt-5">
              <AdminCatalogoProdutoEditar
                key={skuAberto}
                catalogo={catalogo}
                produto={skus.find((p) => p.slug === skuAberto)!}
                recarregar={recarregar}
                aoFechar={() => abrirSku(null)}
              />
            </div>
          ) : (
            <p className="mt-4 text-sm text-alerta-tinta" data-sku-inexistente>
              O produto <code>{skuAberto}</code> não é deste curso — nada foi aberto para edição.
            </p>
          ))}

        {/* A sub-aba Vendas saiu em 21/09, e com ela a única tela que LISTAVA
            os ids. A contagem fica porque ela é o que muda a conduta aqui — id
            faltando é venda que não concede acesso —, e o "ver em" saiu em vez
            de virar link morto. Quem precisa da lista pergunta ao banco:
            `select cat_admin_catalogo() -> 'ids';` */}
        <p className="mt-4 text-sm text-cinza">
          {ids.length} id(s) da Guru e da TMB apontam para este curso.
        </p>
      </section>

      {/* ── As ferramentas do curso ───────────────────────────────────────── */}
      <section className="surface rise overflow-hidden" role="group" aria-labelledby="titulo-ferramentas-do-curso">
        <div className="border-b border-borda p-5 sm:p-6">
          <h3 id="titulo-ferramentas-do-curso" className="font-display text-lg font-bold text-titulo">
            O que este curso entrega
          </h3>
          <p className="mt-1 text-sm text-cinza">
            {cards.length === 0
              ? "Nenhuma ferramenta própria ligada a este curso."
              : `${cards.length} ferramenta(s).`}{" "}
            Fora da conta: as universais (Aulas e Comunidade), que aparecem em todo curso, e os
            módulos da Faculdade (Minhas Notas e Secretaria do Aluno), fixos em todas as pós e no
            MBA.
          </p>
        </div>
        {cards.length > 0 && (
          <ul className="divide-y divide-borda">
            {cards.map((k) => {
              const s = situacaoDaFerramenta(k);
              return (
                <li key={k.slug} className="p-4 sm:px-6">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <Link
                      to={`/admin/catalogo/vitrine?curso=${encodeURIComponent(c.slug)}&card=${encodeURIComponent(k.slug)}`}
                      data-abrir-ferramenta={k.slug}
                      className="font-semibold text-link underline underline-offset-2"
                    >
                      {k.nome}
                    </Link>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${s.classe}`}
                    >
                      {s.rotulo}
                    </span>
                    <span className="text-xs text-cinza">{k.tipo}</span>
                  </div>
                  {k.descricao && <p className="mt-1 text-sm text-cinza">{k.descricao}</p>}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ── O resto da página ─────────────────────────────────────────────── */}
      {/*
        Fechadas. A ementa de Rotinas tem 11.692 caracteres e a do MBA 19.625 —
        abertas, esta tela passaria de 30 mil pixels, que é a cicatriz do CST
        (20.541px com os grupos abertos) com outro conteúdo. Quem procura a
        ementa sabe que procura; quem abriu a tela para ver o prazo, não.
      */}
      {secoes.length > 0 && (
        <section className="surface rise p-5 sm:p-6" role="group" aria-labelledby="titulo-secoes">
          <h3 id="titulo-secoes" className="font-display text-lg font-bold text-titulo">
            O resto da página
          </h3>
          <p className="mt-1 text-sm text-cinza">
            Na ordem da página. As seções nascem fechadas — a ementa de uma pós passa de dez mil
            caracteres.
          </p>
          <div className="mt-4 space-y-2">
            {secoes.map((s, i) => (
              <details key={i} className="rounded-cf border border-borda" data-secao={s.titulo}>
                <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-tinta">
                  {s.titulo}
                  <span className="ml-2 font-normal text-cinza">
                    {s.texto.length.toLocaleString("pt-BR")} caracteres
                  </span>
                </summary>
                <div className="border-t border-borda px-4 py-3">
                  <Prosa texto={s.texto} />
                </div>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* ── Objetivo, público e egresso ───────────────────────────────────── */}
      {doc && (doc.objetivo || doc.publico_alvo || doc.perfil_egresso) && (
        <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-proposta">
          <h3 id="titulo-proposta" className="font-display text-lg font-bold text-titulo">
            A proposta
          </h3>
          {doc.objetivo && (
            <div className="mt-4">
              <h4 className="text-sm font-bold text-tinta">Objetivo geral</h4>
              <div className="mt-1">
                <Prosa texto={doc.objetivo} />
              </div>
            </div>
          )}
          {doc.publico_alvo && (
            <div className="mt-4">
              <h4 className="text-sm font-bold text-tinta">Público-alvo</h4>
              <div className="mt-1">
                <Prosa texto={doc.publico_alvo} />
              </div>
            </div>
          )}
          {doc.perfil_egresso && (
            <div className="mt-4">
              <h4 className="text-sm font-bold text-tinta">Perfil do egresso</h4>
              <div className="mt-1">
                <Prosa texto={doc.perfil_egresso} />
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── O histórico do produto (000306) ───────────────────────────────── */}
      <HistoricoDoProduto curso={c.slug} versao={versaoHistorico} catalogo={catalogo} />
    </div>
  );
}

/** AAAA-MM-DD → DD/MM/AAAA, sem `new Date`: a coluna é `date`, e o fuso a
 *  poria um dia antes (a cicatriz da data da colação). */
const dia = (iso: string) => iso.slice(0, 10).split("-").reverse().join("/");

/**
 * O CRONOGRAMA de liberação visto pelo PRODUTO (000309, o G3): por turma, o que
 * já foi liberado, o que falta e a próxima data. A leitura só ACOMPANHA — a
 * disciplina não liberada continua aberta para o aluno —, e a tela diz isso.
 *
 * As turmas SEM cronograma viram uma linha só, com os números: a Pós RT tem 21
 * turmas, e 21 linhas dizendo "sem início" esconderiam as duas que importam.
 */
function CronogramaDoProduto({ curso }: { curso: string }) {
  const [turmas, setTurmas] = useState<TurmaDoCronograma[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    void (async () => {
      const r = await lerCronogramaDoProduto(curso);
      if (!vivo) return;
      if (r.ok) setTurmas(r.turmas);
      else setErro(r.detalhe ?? "O cronograma não veio.");
    })();
    return () => {
      vivo = false;
    };
  }, [curso]);

  const comCronograma = (turmas ?? []).filter((t) => t.total > 0 && t.inicio_em);
  // A atual aparece sempre, com ou sem cronograma: é a que recebe matrícula.
  const mostradas = (turmas ?? []).filter((t) => t.atual || (t.total > 0 && t.inicio_em));
  const semCronograma = (turmas ?? []).filter((t) => !mostradas.includes(t));

  return (
    <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-cronograma-produto" data-cronograma-do-produto>
      <h3 id="titulo-cronograma-produto" className="font-display text-lg font-bold text-titulo">
        Cronograma de liberação
      </h3>
      <p className="mt-1 text-sm text-cinza">
        Por turma, o que já foi liberado, o que falta e a próxima data — o início da turma mais o
        prazo de cada disciplina na grade. Esta visão só acompanha: a disciplina ainda não liberada
        continua aberta para o aluno.
      </p>
      {erro && <p className="mt-3 text-sm text-erro-tinta" role="alert">{erro}</p>}
      {!turmas && !erro && <p className="mt-3 text-sm text-cinza">Carregando…</p>}
      {turmas && (
        <>
          <p className="mt-3 text-sm text-tinta" data-turmas-com-cronograma={comCronograma.length}>
            {comCronograma.length} de {turmas.length} turma(s) com cronograma preenchido.
          </p>
          <ul className="mt-3 divide-y divide-borda">
            {mostradas.map((t) => (
              <li key={t.id} className="py-3 text-sm" data-turma-do-cronograma={t.numero}>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="font-semibold text-tinta">Turma {t.numero}</span>
                  {t.atual && (
                    <span className="rounded-full bg-chip px-2 py-0.5 text-xs font-semibold text-tinta">atual</span>
                  )}
                  <span className="text-cinza">
                    {t.total === 0
                      ? "sem grade — nada a liberar"
                      : !t.inicio_em
                        ? "sem data de início — o cronograma não foi preenchido"
                        : t.com_prazo === 0
                          ? `início em ${dia(t.inicio_em)}, mas nenhuma disciplina tem prazo`
                          : `início em ${dia(t.inicio_em)} · ${t.liberadas} de ${t.total} liberada(s)${
                              t.com_prazo < t.total ? ` · ${t.total - t.com_prazo} sem prazo` : ""
                            }`}
                  </span>
                </div>
                {t.inicio_em && t.com_prazo > 0 && (
                  <p className="mt-1 text-xs text-cinza" data-proxima-liberacao={t.proxima?.libera_em ?? ""}>
                    {t.proxima
                      ? `Próxima: ${t.proxima.nome}${t.proxima.modulo != null ? ` (módulo ${t.proxima.modulo})` : ""} em ${dia(t.proxima.libera_em)}.`
                      : "Todas as disciplinas com prazo já foram liberadas."}
                    {t.ultima_liberada && ` Última: ${t.ultima_liberada.nome} em ${dia(t.ultima_liberada.libera_em)}.`}
                  </p>
                )}
              </li>
            ))}
          </ul>
          {semCronograma.length > 0 && (
            <p className="mt-2 text-xs text-cinza" data-turmas-sem-cronograma={semCronograma.length}>
              Sem cronograma: turma(s) {semCronograma.map((t) => t.numero).sort((a, b) => a - b).join(", ")}.
            </p>
          )}
          <p className="mt-3 text-sm">
            <Link
              to={`/admin/faculdade/turmas?curso=${encodeURIComponent(curso)}`}
              className="font-semibold text-link underline underline-offset-2"
              data-preencher-cronograma
            >
              Preencher o cronograma em Faculdade › Turmas
            </Link>
          </p>
        </>
      )}
    </section>
  );
}

/**
 * O HISTÓRICO do produto: a página, os documentos e os SKUs do curso, do mais
 * novo para o mais velho — o que a ficha da ferramenta tem desde a 000299. A
 * frase de cada linha é do BANCO; a tela não a escreve.
 */
function HistoricoDoProduto({ curso, versao, catalogo }: { curso: string; versao: number; catalogo: Catalogo }) {
  const [mudancas, setMudancas] = useState<MudancaDoProduto[] | null>(null);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    let vivo = true;
    void (async () => {
      const r = await lerHistoricoDoProduto(curso);
      if (!vivo) return;
      setMudancas(r.ok ? (r.mudancas ?? []) : []);
      setTotal(r.ok ? (r.total ?? 0) : 0);
    })();
    return () => {
      vivo = false;
    };
  }, [curso, versao, catalogo]);

  return (
    <section className="surface rise p-6 sm:p-7" role="group" aria-labelledby="titulo-historico-produto">
      <h3 id="titulo-historico-produto" className="font-display text-lg font-bold text-titulo">
        Histórico do produto
      </h3>
      <p className="mt-1 text-sm text-cinza">
        Quem mexeu na página, nos documentos e nos SKUs deste curso por este painel, quando e por
        quê — gravado pelo banco a cada ato.
      </p>
      {mudancas === null ? (
        <p className="mt-3 text-sm text-cinza">Carregando…</p>
      ) : mudancas.length === 0 ? (
        <p className="mt-3 text-sm text-cinza" data-historico-do-produto="0">
          Nenhuma mudança registrada ainda.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm" data-historico-do-produto={total}>
            <thead>
              <tr className="border-b border-borda text-left text-xs text-cinza">
                <th className="py-2 pr-3 font-semibold">Quando</th>
                <th className="py-2 pr-3 font-semibold">O que aconteceu</th>
                <th className="py-2 pr-3 font-semibold">Por quê</th>
                <th className="py-2 font-semibold">Quem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borda">
              {mudancas.map((m) => (
                <tr key={m.id} className="align-top" data-mudanca-do-produto={m.id}>
                  <td className="whitespace-nowrap py-2 pr-3 text-cinza">{new Date(m.em).toLocaleString("pt-BR")}</td>
                  <td className="py-2 pr-3 text-tinta" data-resumo>
                    {m.resumo}
                  </td>
                  <td className="py-2 pr-3 text-cinza">{m.motivo ?? "—"}</td>
                  <td className="whitespace-nowrap py-2 text-cinza">{m.por}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
